import { Listbox } from '@headlessui/react';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { VariantProps } from 'tailwind-variants';
import { Typography } from '../../molecules/Typography';
import { Icon } from '../Icon';
import {
  errorTextClasses,
  helperTextClasses,
  labelClasses,
  listBoxButtonClasses,
  listBoxButtonIconClasses,
  listBoxClasses,
  listBoxOptionClasses,
  listBoxOptionsClasses,
  selectGroupClasses,
} from './variants';

type iOption = {
  value: string | number;
  label: string;
  disabled?: boolean;
  quantity?: number;
};

type iSelectProps = VariantProps<typeof selectGroupClasses> & {
  options: iOption[];
  helperText?: string;
  label?: string;
  disabled?: boolean;
  value?: iOption | null;
  error?: boolean;
  errorMessage?: string;
  onChange?: (option: iOption) => void;
  ref?: any;
  className?: string;
  showFilterInput?: boolean;
  maxNumberOfOptions?: number;
  filterInputDelay?: number;
};

function Select({
  options,
  size = 'md',
  helperText,
  label,
  disabled = false,
  value,
  error = false,
  errorMessage,
  className,
  ref,
  onChange,
  showFilterInput = false,
  maxNumberOfOptions = 15,
  filterInputDelay = 300,
}: iSelectProps) {
  const [selectedOption, setSelectedOption] = useState<iOption | null>(
    value ?? null
  );

  const [hasMoreOptions, setHasMoreOptions] = useState<boolean>(false);
  const [filterQuery, setFilterQuery] = useState<string>('');
  const [filteredOptions, setFilteredOptions] = useState<iOption[]>(() => {
    if (showFilterInput) {
      setHasMoreOptions(options.length > maxNumberOfOptions);
      return options.slice(0, maxNumberOfOptions);
    }
    return options;
  });

  const [inputTimeout, setInputTimeout] = useState<NodeJS.Timeout>();

  const onInputKeyUp = () => {
    if (inputTimeout) {
      clearTimeout(inputTimeout);
    }
    const newInputTimeout = setTimeout(() => {
      handleFilter(filterQuery);
    }, filterInputDelay);
    setInputTimeout(newInputTimeout);
  };

  const handleFilter = useCallback(
    (query: string) => {
      const filtered = query
        ? options.filter((option) =>
          option.label.toLowerCase().includes(query.toLowerCase())
        )
        : options;
      setHasMoreOptions(filtered.length > maxNumberOfOptions);
      setFilteredOptions(filtered.slice(0, maxNumberOfOptions));
    },
    [options]
  );

  useEffect(() => {
    if (showFilterInput) {
      setFilteredOptions(options.slice(0, maxNumberOfOptions));
      return;
    }
    setFilteredOptions(options);
  }, [options]);

  const queryInputRef = useRef<HTMLInputElement>(null);

  const hasErrorMessage = !!errorMessage;
  const hasError = error || !!errorMessage;

  useEffect(() => {
    if (value !== undefined) {
      setSelectedOption(value);
      setFilterQuery(value ? value.label : '');
    }
  }, [value]);

  return (
    <div className={selectGroupClasses({ size, disabled, className })}>
      <Listbox
        ref={ref}
        value={selectedOption}
        onChange={(option: iOption) => {
          setSelectedOption(option);
          if (showFilterInput) {
            setFilterQuery(option.label);
          }
          if (onChange) {
            onChange(option);
          }
        }}
        as='div'
        className={listBoxClasses({ size, disabled })}
        disabled={disabled}
      >
        {({ open }) => (
          <>
            <Listbox.Button
              className={listBoxButtonClasses({ size, disabled, hasError })}
              onClick={() => {
                if (showFilterInput) {
                  setFilterQuery('');
                  handleFilter('');
                }
              }}
            >
              {!showFilterInput && <p>{selectedOption?.label ?? ''}</p>}
              {showFilterInput && (
                <input
                  ref={queryInputRef}
                  type='text'
                  value={filterQuery}
                  className='w-full border-none p-0 focus:outline-none focus:ring-0 bg-mono-color-white border-nono-color-dark'
                  placeholder={open ? 'Digite aqui para pesquisar...' : ''}
                  onChange={(e) => setFilterQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === ' ') {
                      e.stopPropagation();
                    }
                  }}
                  onKeyUp={onInputKeyUp}
                />
              )}
              <Icon
                Icon={'arrow_drop_down'}
                type='material'
                className={listBoxButtonIconClasses()}
              />
            </Listbox.Button>

            {helperText && !hasErrorMessage && (
              <span className={helperTextClasses({ disabled })}>{helperText}</span>
            )}

            {hasErrorMessage && (
              <span className={errorTextClasses({ disabled })}>{errorMessage}</span>
            )}

            <Listbox.Options
              className={listBoxOptionsClasses({ size, disabled })}
              onFocus={() => {
                if (showFilterInput) {
                  queryInputRef.current?.focus();
                }
              }}
            >
              {filteredOptions.length === 0 && (
                <Listbox.Option
                  key={0}
                  value={0}
                  disabled={true}
                  className={listBoxOptionClasses({
                    disabled: true,
                    selected: false,
                    active: false,
                  })}
                >
                  Nenhum item encontrado
                </Listbox.Option>
              )}
              {filteredOptions.map((option) => (
                <Listbox.Option
                  as={Fragment}
                  key={option.value}
                  value={option}
                  disabled={option.disabled}
                >
                  {({ active }) => (
                    <li
                      className={listBoxOptionClasses({
                        disabled: option.disabled,
                        selected: selectedOption
                          ? selectedOption.value === option.value
                          : false,
                        active,
                      })}
                    >
                      {option.label}
                    </li>
                  )}
                </Listbox.Option>
              ))}

              {hasMoreOptions && (
                <Typography
                  size='mini'
                  className='border-t-hairline border-mono-color-light pt-2 text-center'
                >
                  Este não é o fim da lista, continue digitando para encontrar o
                  item desejado.
                </Typography>
              )}
            </Listbox.Options>

            {label && (
              <Listbox.Label
                className={labelClasses({
                  size,
                  isSelected: !!selectedOption,
                  disabled,
                  hasError,
                })}
              >
                {label}
              </Listbox.Label>
            )}
          </>
        )}
      </Listbox>
    </div>
  );
}

export { Select };
export type { iOption };
